/**
 * Background tasks in the chat (agent-core ai-chat.tasks).
 *
 * The agent's long work — shell and GPU jobs, subthreads, Mac jobs, setup runs,
 * QA benches — runs as tasks that tell the thread when they end. This file is
 * the client's half:
 *
 *   - `useTaskEventsConnection` follows GET /threads/:id/tasks/events, which
 *     pushes the thread's tasks and its running turn as they change, wherever
 *     the change happened (a job's callback, a wake turn in a container).
 *   - The runtime reads the running turn from the store and reattaches to a
 *     turn it did not start: the wake turn a finished job sets off.
 *   - `BackgroundTasksBar` shows what is running, with a stop button.
 *   - `TaskNotificationNotice` renders the notification message a wake turn
 *     starts with as a system notice, not as something the user typed.
 */
import { createContext, useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { CheckCircle2, CircleSlash, Loader2, Square, XCircle, HelpCircle, BellRing } from 'lucide-react';
import { api } from '@/services/api';
import { toast } from '@/components/ui/Toast';
import type { ActiveTurn } from './adapters/firestore-thread-adapter';

export type TaskStatus = 'running' | 'completed' | 'failed' | 'stopped' | 'lost';

export interface ThreadTask {
  taskId: string;
  kind: string;
  label: string;
  status: TaskStatus;
  startedAt: string;
  finishedAt?: string;
  exitCode?: number;
  summary?: string;
  output?: string;
  pendingNotify?: boolean;
  notifiedVia?: 'tool' | 'wake' | 'seen';
  /** A monitor's events so far. */
  eventCount?: number;
}

export interface TaskEventsSnapshot {
  threadId: string | null;
  tasks: ThreadTask[];
  activeTurn: (ActiveTurn & { wake?: boolean }) | null;
  connected: boolean;
}

// ── Store ────────────────────────────────────────────────────────────────────

export interface TaskEventsStore {
  get: () => TaskEventsSnapshot;
  set: (next: Partial<TaskEventsSnapshot>) => void;
  subscribe: (fn: () => void) => () => void;
}

export function createTaskEventsStore(): TaskEventsStore {
  let snap: TaskEventsSnapshot = { threadId: null, tasks: [], activeTurn: null, connected: false };
  const subs = new Set<() => void>();
  return {
    get: () => snap,
    set: (next) => {
      snap = { ...snap, ...next };
      for (const fn of [...subs]) fn();
    },
    subscribe: (fn) => {
      subs.add(fn);
      return () => subs.delete(fn);
    },
  };
}

export const TaskEventsContext = createContext<TaskEventsStore | null>(null);

export function useTaskEvents(store: TaskEventsStore | null): TaskEventsSnapshot {
  const fallback = useMemo(createTaskEventsStore, []);
  const s = store ?? fallback;
  return useSyncExternalStore(s.subscribe, s.get);
}

// ── Connection ───────────────────────────────────────────────────────────────

/**
 * Follow the thread's task events while it is open. Reconnects with a fresh
 * token after an error (an ID token lasts an hour; EventSource would retry
 * with the old one forever).
 */
export function useTaskEventsConnection(
  store: TaskEventsStore,
  threadId: string | null,
  getToken: () => Promise<string | null>,
): void {
  useEffect(() => {
    store.set({ threadId, tasks: [], activeTurn: null, connected: false });
    if (!threadId) return;
    let es: EventSource | null = null;
    let stopped = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;

    const connect = async () => {
      if (stopped) return;
      const token = await getToken().catch(() => null);
      if (stopped) return;
      const streamApiUrl = (import.meta as any).env?.VITE_API_STREAM_URL ?? '';
      const url = `${streamApiUrl}/api/chat/threads/${threadId}/tasks/events${token ? `?token=${encodeURIComponent(token)}` : ''}`;
      es = new EventSource(url, { withCredentials: !streamApiUrl });
      es.addEventListener('open', () => {
        attempt = 0;
        store.set({ connected: true });
      });
      es.addEventListener('tasks', (ev) => {
        try {
          const data = JSON.parse((ev as MessageEvent).data) as { tasks?: ThreadTask[]; activeTurn?: TaskEventsSnapshot['activeTurn'] };
          store.set({ threadId, tasks: data.tasks ?? [], activeTurn: data.activeTurn ?? null });
        } catch { /* a malformed frame is skipped; the next one replaces it */ }
      });
      es.onerror = () => {
        es?.close();
        es = null;
        store.set({ connected: false });
        if (stopped) return;
        attempt++;
        retry = setTimeout(() => { void connect(); }, Math.min(30_000, 1_000 * 2 ** Math.min(attempt, 5)));
      };
    };
    void connect();
    return () => {
      stopped = true;
      if (retry) clearTimeout(retry);
      es?.close();
    };
  }, [store, threadId, getToken]);
}

// ── Desktop notification when a woken turn finishes ──────────────────────────

/** Ask once, the first time the user starts something that may run in the background. */
export function primeTaskNotifications(): void {
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') void Notification.requestPermission();
  } catch { /* unsupported */ }
}

export function notifyWakeTurnDone(title: string, body: string): void {
  try {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const n = new Notification(title, { body, tag: 'agent-task' });
    n.onclick = () => { window.focus(); n.close(); };
  } catch { /* unsupported */ }
}

// ── The bar above the composer ───────────────────────────────────────────────

function StatusIcon({ status }: { status: TaskStatus }) {
  switch (status) {
    case 'running': return <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-500" />;
    case 'completed': return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />;
    case 'failed': return <XCircle className="w-3.5 h-3.5 text-red-600" />;
    case 'stopped': return <CircleSlash className="w-3.5 h-3.5 text-zinc-500" />;
    default: return <HelpCircle className="w-3.5 h-3.5 text-amber-600" />;
  }
}

function elapsed(fromIso: string, toIso?: string): string {
  const ms = Math.max(0, (toIso ? Date.parse(toIso) : Date.now()) - Date.parse(fromIso));
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}m ${s % 60}s` : `${Math.floor(m / 60)}h ${m % 60}m`;
}

/** Ended tasks stay in the bar this long, so an end the user was watching for is seen. */
const RECENT_MS = 90_000;

export function BackgroundTasksBar({ threadId }: { threadId: string | null }) {
  const store = useContext(TaskEventsContext);
  const { tasks, threadId: storeThread } = useTaskEvents(store);
  const [, tick] = useState(0);
  const [stopping, setStopping] = useState<Set<string>>(new Set());
  const anyRunning = tasks.some((t) => t.status === 'running');
  // Ask once, when there is first something to be told about.
  useEffect(() => { if (anyRunning) primeTaskNotifications(); }, [anyRunning]);
  const anyTasks = tasks.length > 0;
  useEffect(() => {
    if (!anyTasks) return;
    const t = setInterval(() => tick((n) => n + 1), 1_000);
    return () => clearInterval(t);
  }, [anyTasks]);
  if (!threadId || storeThread !== threadId) return null;
  const now = Date.now();
  const shown = tasks.filter((t) => t.status === 'running'
    || (t.finishedAt && now - Date.parse(t.finishedAt) < RECENT_MS));
  if (shown.length === 0) return null;
  const running = shown.filter((t) => t.status === 'running').length;

  const stop = async (taskId: string) => {
    setStopping((s) => new Set(s).add(taskId));
    try {
      await api.post(`/chat/threads/${threadId}/tasks/${encodeURIComponent(taskId)}/stop`, {});
    } catch (err: any) {
      toast.error(`Could not stop it: ${String(err?.message ?? err)}`);
    } finally {
      setStopping((s) => { const n = new Set(s); n.delete(taskId); return n; });
    }
  };

  return (
    <div className="mb-2 rounded-lg border border-zinc-200 bg-zinc-50/70 px-2.5 py-1.5 text-xs text-zinc-700" data-testid="background-tasks">
      <div className="mb-1 flex items-center gap-1.5 font-medium text-zinc-500">
        <BellRing className="w-3 h-3" />
        {running > 0
          ? `${running} running in the background — the agent is told when each ends`
          : 'Background tasks ended'}
      </div>
      <ul className="flex flex-col gap-0.5">
        {shown.map((t) => (
          <li key={t.taskId} className="flex items-center gap-2 min-w-0" title={t.summary ?? t.label}>
            <StatusIcon status={t.status} />
            <span className="shrink-0 rounded bg-white px-1 py-px text-[10px] uppercase tracking-wide text-zinc-500 border border-zinc-200">{t.kind}</span>
            <span className="truncate font-mono text-[11px]">{t.label}</span>
            <span className="ml-auto shrink-0 tabular-nums text-zinc-400">
              {t.eventCount ? `${t.eventCount} event${t.eventCount === 1 ? '' : 's'} · ` : ''}
              {t.status === 'running' ? elapsed(t.startedAt) : `${t.status} · ${elapsed(t.startedAt, t.finishedAt)}`}
            </span>
            {t.status === 'running' && (
              <button
                type="button"
                onClick={() => void stop(t.taskId)}
                disabled={stopping.has(t.taskId)}
                className="shrink-0 rounded p-0.5 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-800 disabled:opacity-40"
                title="Stop this task"
              >
                {stopping.has(t.taskId) ? <Loader2 className="w-3 h-3 animate-spin" /> : <Square className="w-3 h-3" />}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── The notification message ─────────────────────────────────────────────────

const TAG = 'task-notification';

export function isTaskNotificationText(text: unknown): text is string {
  return typeof text === 'string' && text.trimStart().startsWith(`<${TAG}>`);
}

interface ParsedTask {
  id: string;
  kind: string;
  status: TaskStatus;
  exitCode?: string;
  label: string;
  summary?: string;
}

const unescape = (s: string) => s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');

export interface ParsedEvent {
  task: string;
  monitor: string;
  at: string;
  text: string;
}

export function parseTaskEvents(text: string): ParsedEvent[] {
  const out: ParsedEvent[] = [];
  const re = /<event ([^>]*)>\n?([\s\S]*?)\n?<\/event>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const attrs: Record<string, string> = {};
    for (const a of m[1].matchAll(/([a-z-]+)="([^"]*)"/g)) attrs[a[1]] = unescape(a[2]);
    out.push({ task: attrs.task ?? '', monitor: attrs.monitor ?? '', at: attrs.at ?? '', text: unescape(m[2]) });
  }
  return out;
}

export function parseTaskNotification(text: string): ParsedTask[] {
  const out: ParsedTask[] = [];
  const re = /<task ([^>]*)>([\s\S]*?)<\/task>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const attrs: Record<string, string> = {};
    for (const a of m[1].matchAll(/([a-z-]+)="([^"]*)"/g)) attrs[a[1]] = unescape(a[2]);
    const body = m[2];
    const label = /<label>([\s\S]*?)<\/label>/.exec(body)?.[1];
    const summary = /<summary>\n?([\s\S]*?)\n?<\/summary>/.exec(body)?.[1];
    out.push({
      id: attrs.id ?? '',
      kind: attrs.kind ?? 'task',
      status: (attrs.status as TaskStatus) ?? 'completed',
      exitCode: attrs['exit-code'],
      label: unescape(label ?? attrs.id ?? ''),
      summary: summary ? unescape(summary) : undefined,
    });
  }
  return out;
}

export function TaskNotificationNotice({ text }: { text: string }) {
  const tasks = parseTaskNotification(text);
  const events = parseTaskEvents(text);
  const [open, setOpen] = useState<string | null>(null);
  const heading = [
    events.length ? (events.length === 1 ? 'A monitor sent an event' : `Monitors sent ${events.length} events`) : '',
    tasks.length ? (tasks.length === 1 ? 'a background task ended' : `${tasks.length} background tasks ended`) : '',
  ].filter(Boolean).join(', and ');
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col px-4 pt-6 sm:px-6" data-testid="task-notification">
      <div className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 px-3 py-2 text-xs text-zinc-600">
        <div className="mb-1 flex items-center gap-1.5 font-medium text-zinc-500">
          <BellRing className="w-3.5 h-3.5" />
          {heading.charAt(0).toUpperCase() + heading.slice(1)}
        </div>
        {events.length > 0 && (
          <ul className="mb-1 flex flex-col gap-1">
            {events.map((e, i) => (
              <li key={`${e.task}-${i}`} className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="shrink-0 rounded bg-white px-1 py-px text-[10px] uppercase tracking-wide text-zinc-500 border border-zinc-200">event</span>
                  <span className="truncate text-[11px] text-zinc-500">{e.monitor}</span>
                </div>
                <pre className="mt-0.5 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-white p-1.5 font-mono text-[11px] text-zinc-700 border border-zinc-200">{e.text}</pre>
              </li>
            ))}
          </ul>
        )}
        <ul className="flex flex-col gap-1">
          {tasks.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                className="flex w-full items-center gap-2 text-left hover:text-zinc-900"
                onClick={() => setOpen(open === t.id ? null : t.id)}
              >
                <StatusIcon status={t.status} />
                <span className="shrink-0 rounded bg-white px-1 py-px text-[10px] uppercase tracking-wide text-zinc-500 border border-zinc-200">{t.kind}</span>
                <span className="truncate font-mono text-[11px]">{t.label}</span>
                <span className="ml-auto shrink-0 text-zinc-400">{t.status}{t.exitCode !== undefined ? ` · exit ${t.exitCode}` : ''}</span>
              </button>
              {open === t.id && t.summary && (
                <pre className="mt-1 max-h-60 overflow-auto whitespace-pre-wrap rounded bg-white p-2 font-mono text-[11px] text-zinc-700 border border-zinc-200">{t.summary}</pre>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
